import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, Tabs, Tab, Divider, alpha } from '@mui/material';
import { bgBlur } from '../../utils/cssStyles';
import useOffSetTop from '../../hooks/useOffSetTop';
import { HEADER } from '../../config-global';
import { useAuthContext } from '../../auth/useAuthContext';
import { useDispatch, useSelector } from '../../redux/store';
import { fetchSubOrgById } from '../../redux/features/subOrganization/subOrgSlice';
import { selectSubOrg } from '../../redux/features/subOrganization/subOrgSelectors';
import useLocales from '../../locales/useLocales';
import Logo from '../../components/logo';
import AccountPopover from '../dashboard/header/AccountPopover';
import LanguagePopover from '../dashboard/header/LanguagePopover';
import Iconify from '../../components/iconify';
import { PATH_AGENT, PATH_VOICE_AGENT } from '../../routes/paths';

const TABS_HEIGHT = 44;

function AgentHeader() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP);

  const isVoiceQueue = location.pathname.startsWith('/agent/voice-queue');
  const currentTab = isVoiceQueue ? 1 : 0;

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({ color: theme.palette.background.default }),
        width: '100%',
      }}
    >
      {/* Top row: logo + actions */}
      <Toolbar
        sx={{
          height: isOffset ? HEADER.H_DASHBOARD_DESKTOP_OFFSET : HEADER.H_DASHBOARD_DESKTOP,
          px: { xs: 2, lg: 5 },
          transition: theme.transitions.create(['height'], {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Logo sx={{ mr: 2.5 }} />
        <Stack
          flexGrow={1}
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={{ xs: 0.5, sm: 1.5 }}
        >
          <LanguagePopover />
          <AccountPopover />
        </Stack>
      </Toolbar>

      <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.1) }} />

      {/* Bottom row: navigation tabs */}
      <Box sx={{ px: { xs: 2, lg: 5 }, height: TABS_HEIGHT, display: 'flex', alignItems: 'flex-end' }}>
        <Tabs
          value={currentTab}
          onChange={(_, v) =>
            navigate(v === 0 ? PATH_AGENT.orders : PATH_VOICE_AGENT.dashboard)
          }
          sx={{
            minHeight: TABS_HEIGHT,
            '& .MuiTabs-indicator': {
              height: 2.5,
              borderRadius: '2.5px 2.5px 0 0',
              bgcolor: currentTab === 1 ? 'warning.main' : 'primary.main',
              transition: 'all 0.3s ease',
            },
          }}
        >
          <Tab
            label="Commandes"
            icon={<Iconify icon="mdi:receipt-text-outline" width={16} />}
            iconPosition="start"
            sx={{
              minHeight: TABS_HEIGHT,
              px: 2,
              fontSize: '0.8125rem',
              fontWeight: 600,
              textTransform: 'none',
              gap: 0.75,
              color: 'text.secondary',
              '&.Mui-selected': { color: 'primary.main' },
            }}
          />
          <Tab
            label="File vocale"
            icon={<Iconify icon="mdi:headset" width={16} />}
            iconPosition="start"
            sx={{
              minHeight: TABS_HEIGHT,
              px: 2,
              fontSize: '0.8125rem',
              fontWeight: 600,
              textTransform: 'none',
              gap: 0.75,
              color: 'text.secondary',
              '&.Mui-selected': { color: 'warning.main' },
            }}
          />
        </Tabs>
      </Box>
    </AppBar>
  );
}

export default function AgentLayout() {
  const { user } = useAuthContext();
  const dispatch = useDispatch();
  const subOrg = useSelector(selectSubOrg);
  const { onChangeLang } = useLocales();
  const langApplied = useRef(false);

  useEffect(() => {
    if (user?.subOrgId) dispatch(fetchSubOrgById(user.subOrgId));
  }, [user?.subOrgId, dispatch]);

  useEffect(() => {
    if (!subOrg?.defaultLanguage || langApplied.current) return;
    if (subOrg.id !== user?.subOrgId) return;
    onChangeLang(subOrg.defaultLanguage.toLowerCase());
    langApplied.current = true;
  }, [subOrg?.defaultLanguage, subOrg?.id, user?.subOrgId, onChangeLang]);

  return (
    <>
      <AgentHeader />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: `${HEADER.H_DASHBOARD_DESKTOP + TABS_HEIGHT + 1}px`,
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Outlet />
      </Box>
    </>
  );
}
